import { useCallback, useEffect, useRef, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { ICONS } from "../constants/icons";
import useMoveTo from "../constants/navigation";
import { useParams, useSearchParams } from "react-router";
import {
  ref,
  onValue,
  off,
  push,
  set,
  update,
  get,
  query,
  orderByChild,
  equalTo,
} from "firebase/database";
import { FiCamera, FiVideo } from "react-icons/fi";
import {
  compressImage,
  copyToClipboard,
  formatDate,
  getChatId,
  handleDownloadImage,
} from "../utils/utils";
import { useUser } from "../contexts/UserContext";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { database, storage } from "../constants/firebase";
import AddGroupMembersBottomSheet from "../components/AddGroupMembersBottomSheet";
import { useCall } from "../contexts/CallContext";
import { ROUTES } from "../App";
import useKeyboardOpen from "../hooks/useKeyboardOpen";
import { BsThreeDotsVertical } from "react-icons/bs";
import ViewMembers from "../components/ViewMembers";
import TypingAnimation from "../components/TypingAnimation";
import ReactionBox from "../components/ReactionBox";
import MessageBubble from "../components/MessageBubble";
import MessageInput from "../components/MessageInput";
import RecordingPopup from "../components/RecordingPopup";
import { useChatMessages } from "../hooks/useChatMessages";
import { useChatScroll } from "../hooks/useChatScroll";
import { useMessageActions } from "../hooks/useMessageActions";
import { useTypingStatus } from "../hooks/useTypingStatus";
import { useRecording } from "../hooks/useRecording";
import { formatGroupedMessages, groupMessagesByDate } from "../utils/chatUtils";
import ForwardSharePopup from "../components/ForwardSharePopup";

const GroupChat = () => {
  const navigate = useMoveTo();
  const { groupId } = useParams();
  const { currentUser } = useUser();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [copied, setCopied] = useState("");
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [users, setUsers] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [isAddMembersOpen, setIsAddMembersOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const [loadedImages, setLoadedImages] = useState({});
  const [invitePopupOpen, setInvitePopupOpen] = useState(false);
  const [existingInviteLink, setExistingInviteLink] = useState(null);
  const [totalMembers, setTotalMembers] = useState(0);
  const [groupMembers, setGroupMembers] = useState([]);
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const isKeyboardOpen = useKeyboardOpen();

  const {
    setCallStatus,
    callObject,
    setIsOutgoingCall,
    setActiveCallData,
    setCallRoomUrl,
    setCallType,
    setIsOnCall
  } = useCall();



  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  // Custom hooks
  const { messages, loading: messagesLoading, deleteMessage, addReaction, removeReaction } =
    useChatMessages('group', groupId, currentUser);

  const { setContainerRef } = useChatScroll(messages);

  const {
    selectionMode,
    setSelectionMode,
    selectedMessage,
    setSelectedMessage,
    selectedMessageId,
    setSelectedMessageId,
    reactionPosition,
    editingMessage,
    cancelEdit,
    editMessageText,
    setEditMessageText,
    replyingTo,
    swipeProgress,
    reactionBoxRef,
    longPressTimeout,
    longPressTriggered,
    isScrollingRef,
    openReactionBox,
    closeReactionBox,
    handleEditMessage,
    handleReplyToMessage,
    clearReply,
    handleSwipeStart,
    handleSwipeMove,
    handleSwipeEnd
  } = useMessageActions(currentUser);

  // Get member IDs for typing status
  const memberIds = selectedGroup?.members ? Object.keys(selectedGroup.members).filter(
    id => id !== currentUser.uid
  ) : [];

  const { isTyping, updateTypingStatus } = useTypingStatus('group', groupId, currentUser, memberIds);

  const {
    isRecording,
    recordingPopup,
    startRecording,
    stopRecording,
    cancelRecording,
    uploadAudio
  } = useRecording('group', groupId, currentUser);

  // Define sendMessage locally
  const sendMessage = useCallback(async (messageData) => {
    if (!groupId || !currentUser) return null;

    const messagesRef = ref(database, `groupChats/${groupId}`);
    const newMessageRef = push(messagesRef);

    const defaultData = {
      senderId: currentUser.uid,
      timestamp: Date.now(),
      seenBy: { [currentUser.uid]: true }
    };

    await set(newMessageRef, { ...defaultData, ...messageData });
    return newMessageRef.key;
  }, [groupId, currentUser]);

  const autoResizeTextarea = (el) => {
    el.style.height = "auto";
    const maxHeight = 112;
    if (el.scrollHeight > maxHeight) {
      el.style.height = maxHeight + "px";
      el.style.overflowY = "auto";
    } else {
      el.style.height = el.scrollHeight + "px";
      el.style.overflowY = "hidden";
    }
  };

  const resetTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  // Update total members when selectedGroup or users change
  useEffect(() => {
    if (!selectedGroup) return;

    const total = Object.keys(selectedGroup.members || {}).length;
    const members = Object.keys(selectedGroup?.members || {})
      .map(uid => ({
        uid,
        ...users[uid]
      }))
      .filter(Boolean);

    setGroupMembers(members);
    setTotalMembers(total);
  }, [selectedGroup, users]);

  // Check invite token
  useEffect(() => {
    if (!inviteToken || !currentUser || !selectedGroup) return;

    // If current user is the host, do nothing (don't show invite popup)
    if (currentUser.uid === selectedGroup.createdBy) {
      clearInviteParam(); // Remove invite param from URL
      return;
    }

    const checkMembership = async () => {
      const memberSnap = await get(ref(database, `Groups/${groupId}/members/${currentUser.uid}`));
      if (memberSnap.exists()) {
        const isMember = memberSnap.val();
        if (isMember) {
          window.showToast("Already in this group", "error");
          clearInviteParam();
        } else {
          setInvitePopupOpen(true);
        }
      } else {
        setInvitePopupOpen(true);
      }
    };

    checkMembership();
  }, [inviteToken, currentUser, selectedGroup, groupId]);

  const handleInviteJoin = async (token) => {
    try {
      const inviteSnap = await get(ref(database, `Invites/${token}`));
      if (!inviteSnap.exists()) {
        window.showToast("Invalid invite link", "error");
        return;
      }

      const { targetType, targetId } = inviteSnap.val();

      if (targetType === "group" && targetId !== groupId) {
        window.showToast("Invite does not match this page", "error");
        return;
      }

      if (targetType === "group") {
        await joinGroupWithCheck(targetId);
      }
    } catch (err) {
      console.error(err);
      window.showToast("Failed to join", "error");
    }
  };

  const joinGroupWithCheck = async (groupId) => {
    const memberRef = ref(database, `Groups/${groupId}/members/${currentUser.uid}`);
    const snap = await get(memberRef);

    if (snap.exists() && snap.val()) {
      window.showToast("Already in this group", "error");
      return;
    }

    const updates = {};
    updates[`Groups/${groupId}/members/${currentUser.uid}`] = true;
    updates[`Users/${currentUser.uid}/groups/${groupId}`] = true;

    await update(ref(database), updates);
    window.showToast("Joined group", "success");
    clearInviteParam();
  };

  const clearInviteParam = () => {
    navigate(`${ROUTES.GROUP_CHAT}/${groupId}`, { replace: true });
  };

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editingMessage) return;
      if (event.button === 2) return;

      if (!editingMessage) {
        setSelectionMode(false);
        setSelectedMessage(null);
      }

      if (reactionBoxRef.current && !reactionBoxRef.current.contains(event.target)) {
        setSelectedMessageId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [selectedMessageId, editingMessage]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutsideMenu = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideMenu);
    document.addEventListener("touchstart", handleClickOutsideMenu);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideMenu);
      document.removeEventListener("touchstart", handleClickOutsideMenu);
    };
  }, [isMenuOpen]);

  // Fetch group data
  useEffect(() => {
    if (!groupId) return;

    const groupRef = ref(database, `Groups/${groupId}`);
    const unsubscribe = onValue(groupRef, (snapshot) => {
      if (snapshot.exists()) {
        const groupData = snapshot.val();
        setSelectedGroup(groupData);

        if (groupData.members) {
          Object.keys(groupData.members).forEach((uid) => {
            const userRef = ref(database, `Users/${uid}`);
            onValue(userRef, (userSnap) => {
              if (userSnap.exists()) {
                setUsers((prev) => ({
                  ...prev,
                  [uid]: userSnap.val(),
                }));
              }
            });
          });
        }
      } else {
        setSelectedGroup(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [groupId]);

  // Fetch all users
  useEffect(() => {
    const usersRef = ref(database, `Users`);
    const unsub = onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([uid, u]) => ({ uid, ...u }));
        setAllUsers(list);
      } else {
        setAllUsers([]);
      }
    });

    return () => off(usersRef);
  }, []);

  // Load existing invite
  useEffect(() => {
    if (!currentUser || !groupId) return;

    const loadExistingInvite = async () => {
      try {
        const targetType = "group";
        const compositeKey = `${targetType}_${groupId}_${currentUser.uid}`;
        const invitesRef = ref(database, "Invites");
        const inviteQuery = query(
          invitesRef,
          orderByChild("compositeKey"),
          equalTo(compositeKey)
        );

        const snapshot = await get(inviteQuery);

        if (snapshot.exists()) {
          const inviteKey = Object.keys(snapshot.val())[0];
          const inviteUrl = `${window.location.origin}/#/group-chat/${groupId}?invite=${inviteKey}`;
          setExistingInviteLink(inviteUrl);
        }
      } catch (err) {
        console.error("Invite preload error:", err);
      }
    };

    loadExistingInvite();
  }, [currentUser, currentUser.uid, groupId]);

  const formatMessages = (messages) => {
    if (!messages.length) return [];

    const formattedMessages = [];
    let lastSenderId = null;

    messages.forEach((message, index) => {
      const showHeader =
        lastSenderId !== message.senderId ||
        (index > 0 && messages[index - 1].senderId !== message.senderId);

      formattedMessages.push({
        ...message,
        showHeader,
      });

      lastSenderId = message.senderId;
    });

    return formattedMessages;
  };

  // Group messages by date
  const groupedMessages = groupMessagesByDate(messages, formatDate);
  const formattedGroupedMessages = formatGroupedMessages(groupedMessages, formatMessages);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const messageData = {
      text: newMessage,
    };

    // Add reply data if replying to a message
    if (replyingTo) {
      const replyTo = {
        messageId: replyingTo.id,
        senderId: replyingTo.senderId,
      };

      if (replyingTo.text) {
        replyTo.text = replyingTo.text;
      } else if (replyingTo.imageUrl) {
        replyTo.imageUrl = replyingTo.imageUrl;
        replyTo.displayText = "📷 Photo";
      } else if (replyingTo.audioUrl) {
        replyTo.audioUrl = replyingTo.audioUrl;
        replyTo.displayText = "🎤 Voice message";
      }

      messageData.replyTo = replyTo;
    }

    await sendMessage(messageData);
    setNewMessage("");
    clearReply();
    resetTextareaHeight();
  };

  const handleImageUpload = async (file) => {
    if (!file || !currentUser || !groupId) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const compressed = await compressImage(file);
      const filename = `${Date.now()}_${compressed.name}`;
      const imageRef = storageRef(storage, `groupChats/${groupId}/${filename}`);

      const uploadTask = uploadBytesResumable(imageRef, compressed);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Error uploading image:", error);
          setIsUploading(false);
        },
        async () => {
          const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);

          await sendMessage({
            imageUrl,
          });

          setIsUploading(false);
          setUploadProgress(0);
        }
      );
    } catch (error) {
      console.error("Error starting upload:", error);
      setIsUploading(false);
    }
  };

  const handleRemoveMembers = async (user) => {
    if (!groupId) return;

    try {
      await set(ref(database, `Groups/${groupId}/members/${user.uid}`), null);
      setSelectedGroup((prev) => {
        if (!prev) return prev;
        const updatedMembers = { ...(prev.members || {}) };
        delete updatedMembers[user.uid];
        return { ...prev, members: updatedMembers };
      });
    } catch (err) {
      console.error("Error removing member:", err);
    }
  };

  const handleAddMembers = async (selectedUsers) => {
    if (!groupId || !selectedUsers || selectedUsers.length === 0) return;

    try {
      for (const user of selectedUsers) {
        await set(ref(database, `Groups/${groupId}/members/${user.uid}`), true);
        setUsers((prev) => ({ ...prev, [user.uid]: user }));
      }

      setSelectedGroup((prev) => ({
        ...prev,
        members: {
          ...(prev?.members || {}),
          ...Object.fromEntries(selectedUsers.map((u) => [u.uid, true])),
        },
      }));

      setIsAddMembersOpen(false);
    } catch (err) {
      console.error("Error adding members:", err);
    }
  };

  const handleInvite = () => {
    if (!currentUser || !groupId) return;

    if (existingInviteLink) {
      copyToClipboard(existingInviteLink)
        .then(() => {
          setCopied("invited");
          setTimeout(() => setCopied(""), 2000);
          window.showToast("Invite link copied", "success");
        })
        .catch(() => window.showToast("Failed to copy invite link", "error"));
      return;
    }

    const targetType = "group";
    const targetId = groupId;
    const compositeKey = `${targetType}_${targetId}_${currentUser.uid}`;
    const invitesRef = ref(database, "Invites");

    const tempInviteRef = push(invitesRef);
    const inviteToken = tempInviteRef.key;
    const inviteUrl = `${window.location.origin}/#/group-chat/${targetId}?invite=${inviteToken}`;

    copyToClipboard(inviteUrl)
      .then(() => {
        setCopied("invited");
        setTimeout(() => setCopied(""), 2000);
        window.showToast("Invite link copied", "success");
      })
      .catch(() => window.showToast("Failed to copy invite link", "error"));

    setExistingInviteLink(inviteUrl);

    (async () => {
      try {
        const inviteQuery = query(
          invitesRef,
          orderByChild("compositeKey"),
          equalTo(compositeKey)
        );

        const snapshot = await get(inviteQuery);

        if (snapshot.exists()) {
          const existingKey = Object.keys(snapshot.val())[0];
          const existingUrl = `${window.location.origin}/#/${targetType}-chat/${targetId}?invite=${existingKey}`;
          setExistingInviteLink(existingUrl);
          await set(tempInviteRef, null);
          return;
        }

        await set(tempInviteRef, {
          targetType,
          targetId,
          createdBy: currentUser.uid,
          compositeKey,
          createdAt: Date.now(),
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleOutgoingGroupCall = async (withVideo = false) => {
    try {
      setIsOutgoingCall(true);
      setCallStatus("calling");
      setCallType(withVideo ? "video" : "audio");

      const resp = await fetch(
        `https://daily-backend-98546189821.us-central1.run.app${ROUTES.CREATE_ROOM_API}`
      );

      const { url } = await resp.json();
      setCallRoomUrl(url);

      const callId = groupId;

      const participants = Object.keys(selectedGroup.members).reduce(
        (acc, uid) => {
          acc[uid] = {
            uid,
            status: uid === currentUser.uid ? "joined" : "ringing",
          };
          return acc;
        },
        {}
      );

      const callData = {
        callId,
        groupId,
        url,
        from: currentUser.uid,
        isVideo: withVideo,
        status: "ringing",
        createdAt: Date.now(),
        participants,
        activeParticipants: 0, // 🔥 IMPORTANT
      };

      await set(ref(database, `groupCallSessions/${callId}`), callData);

      setActiveCallData(callData);

      await callObject.join({ url });

      callObject.setLocalVideo(withVideo);
      callObject.setLocalAudio(true);

      // 🔥 FIRST USER STARTS CALL
      await update(ref(database, `groupCallSessions/${callId}`), {
        status: "connected",
        activeParticipants: 1,
        [`participants/${currentUser.uid}/status`]: "joined",
      });

      setCallStatus("connected");

    } catch (err) {
      console.error(err);
    }
  };


  const handleDeleteMessage = async (messageId) => {
    if (!messageId || !groupId || !currentUser) return;
    const message = messages.find((m) => m.id === messageId);
    if (!message) return;
    await deleteMessage(messageId, message);
    setSelectedMessageId(null);
    setSelectionMode(false);
  };

  const handleReaction = async (messageId, emoji) => {
    await addReaction(messageId, emoji.id);
    setSelectedMessage(null);
    setSelectionMode(false);
  };

  const toggleReaction = async (messageId, emojiId, currentReaction) => {
    if (currentReaction === emojiId) {
      await removeReaction(messageId);
    } else {
      await addReaction(messageId, emojiId);
    }
  };

  const handleUpdateMessage = async () => {
    if (!editMessageText.trim() || !currentUser || !groupId || !editingMessage) return;

    try {
      const messageRef = ref(
        database,
        `groupChats/${groupId}/${editingMessage.id}`
      );

      await update(messageRef, {
        text: editMessageText,
        edited: true,
        editedAt: Date.now()
      });

      // Reset edit state
      cancelEdit();
      setSelectionMode(false);
      // Clear input and reset textarea height
      setNewMessage("");
      resetTextareaHeight();

    } catch (error) {
      console.error("Error updating message:", error);
      window.showToast("Failed to update message", "error");
    }
  };

  const handleReplyToMessageWithSender = (message) => {
    const sender = users[message.senderId];
    handleReplyToMessage(message, null, users);
  };

  if (loading || messagesLoading) {
    return (
      <div className="h-screen flex items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }

  if (!selectedGroup) {
    return (
      <div className="h-screen flex items-center justify-center text-gray-500">
        Group not found
      </div>
    );
  }

  // Get typing status display
  const typingDisplay = () => {
    const typingMembers = Object.keys(isTyping);
    if (typingMembers.length === 0) return null;

    const typingNames = typingMembers
      .map(uid => users[uid]?.name || "Someone")
      .slice(0, 2);

    if (typingMembers.length === 1) {
      return <TypingAnimation className="text-primary text-xs" text={`${typingNames[0]} is typing`} />;
    } else if (typingMembers.length === 2) {
      return <TypingAnimation className="text-primary text-xs" text={`${typingNames[0]} and ${typingNames[1]} are typing`} />;
    } else {
      return <TypingAnimation className="text-primary text-xs" text={`${typingNames[0]} and ${typingMembers.length - 1} others are typing`} />;
    }
  };

  const memberListDisplay = () => {
    if (!selectedGroup?.members || Object.keys(selectedGroup.members).length === 0) {
      return "No members";
    }

    let memberNames = Object.keys(selectedGroup.members).map(
      (uid) => uid === currentUser.uid ? "You" : users[uid]?.name || "Unknown"
    );

    memberNames = memberNames.sort((a, b) => a === "You" ? -1 : b === "You" ? 1 : 0);
    const firstFour = memberNames.slice(0, 4);
    const extraCount = memberNames.length - firstFour.length;

    return (
      <>
        {firstFour.join(", ")}
        {extraCount > 0 && ` +${extraCount} more`}
      </>
    );
  };

  const menuItems = [
    {
      label: "Share Group",
      onClick: () => {
        setIsMenuOpen(false);
        setIsSharePopupOpen(true);
      },
    },
    {
      label: "Edit Group Info",
      onClick: () => navigate(`${ROUTES.CREATE_GROUP}/${groupId}`)
    },
    ...(selectedGroup.createdBy === currentUser.uid ? [{
      label: "Add Members",
      onClick: () => {
        setIsMenuOpen(false);
        setIsAddMembersOpen(true);
      }
    }] : []),
    {
      label: copied === "invited" ? "Copied!" : "Copy Invite Link",
      onClick: handleInvite
    }
  ];

  const handleForwardShare = async (selectedItems) => {
    try {
      // Get the invite link for the current group
      let shareLink = existingInviteLink;
      let inviteToken = null;

      // If no existing invite link, create one
      if (!shareLink) {
        const targetType = "group";
        const targetId = groupId;
        const compositeKey = `${targetType}_${targetId}_${currentUser.uid}`;
        const invitesRef = ref(database, "Invites");

        const tempInviteRef = push(invitesRef);
        inviteToken = tempInviteRef.key;
        shareLink = `${window.location.origin}/#/group-chat/${targetId}?invite=${inviteToken}`;

        await set(tempInviteRef, {
          targetType,
          targetId,
          createdBy: currentUser.uid,
          compositeKey,
          createdAt: Date.now(),
        });

        setExistingInviteLink(shareLink);
      }

      // Prepare share message
      const shareMessage = `Follow this link to join my zylen group: \n${shareLink}`;

      // Share to selected items
      for (const item of selectedItems) {
        if (item.type === 'chat') {
          // Send to private chat
          const chatId = getChatId(currentUser.uid, item.id);
          const messagesRef = ref(database, `privateChats/${chatId}`);
          const newMessageRef = push(messagesRef);

          await set(newMessageRef, {
            senderId: currentUser.uid,
            text: shareMessage,
            timestamp: Date.now(),
            seen: false,
            type: 'forwarded',
            originalGroup: {
              id: groupId,
              name: selectedGroup.name,
              photo: selectedGroup.photo || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346',
              inviteToken: inviteToken
            }
          });
        }
        else if (item.type === 'group') {
          // Send to group chat
          const messagesRef = ref(database, `groupChats/${item.id}`);
          const newMessageRef = push(messagesRef);

          await set(newMessageRef, {
            senderId: currentUser.uid,
            text: shareMessage,
            timestamp: Date.now(),
            seenBy: { [currentUser.uid]: true },
            type: 'forwarded',
            originalGroup: {
              id: groupId,
              name: selectedGroup.name,
              photo: selectedGroup.photo || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346',
              inviteToken: inviteToken
            }
          });
        }
        else if (item.type === 'community') {
          // For communities, you might want to send to community chat or create a post
          // This depends on your community structure
          const communityChatRef = ref(database, `communityMessages/${item.id}`);
          const newMessageRef = push(communityChatRef);

          await set(newMessageRef, {
            senderId: currentUser.uid,
            text: shareMessage,
            timestamp: Date.now(),
            isSeen: { [currentUser.uid]: true },
            type: 'forwarded',
            originalGroup: {
              id: groupId,
              name: selectedGroup.name,
              photo: selectedGroup.photo || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346',
              inviteToken: inviteToken
            }
          });
        }
      }

      window.showToast(`Shared to ${selectedItems.length} chat(s)`, "success");
    } catch (error) {
      console.error("Error sharing group:", error);
      window.showToast("Failed to share group", "error");
    }
  };

  const handleGoBack = () => {
    // Try to go back, if there's no history, go to Chats
    if (window.history.length > 2) { // >2 because typically there's at least the current page and the previous
      navigate(-1);
    } else {
      navigate(ROUTES.CHATS);
    }
  };

  return (
    <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
      <div className="flex">
        <Sidebar />

        <main className="flex-1 h-screen flex flex-col bg-white dark:bg-[#1A1A1A] relative">
          {/* Header */}
          <div className="flex items-center space-x-8 justify-between md:px-6 px-2 pb-3 pt-4 md:pt-8 border-b dark:border-[#2A2A2A] border-[#D9D9D9]/50">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleGoBack}
                className="flex items-center justify-center p-2 flex-shrink-0"
              >
                <img
                  src={ICONS.arrowBack}
                  alt="Back"
                  className="w-5 h-5 brightness-0 dark:invert"
                />
              </button>

              <img
                src={
                  selectedGroup.photo ||
                  "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346"
                }
                alt={selectedGroup.name}
                className="w-10 h-10 rounded-full object-cover cursor-pointer"
                onClick={() => setSelectedImage(selectedGroup.photo)}
              />

              <div className="flex flex-col leading-tight">
                <p className="font-semibold text-base line-clamp-1">
                  {selectedGroup.name}
                </p>
                <button
                  className="flex items-center"
                  onClick={() => setIsMembersModalOpen(true)}
                >
                  <p className="text-xs text-left text-gray-500 line-clamp-1">
                    {typingDisplay() || memberListDisplay()}
                  </p>
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {!window.isInApp && (
                <button
                  className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500/30"
                  onClick={() => handleOutgoingGroupCall(true)}
                >
                  <FiVideo className="w-5 h-5 text-gray-800 dark:text-white" />
                </button>
              )}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500/30"
                >
                  <BsThreeDotsVertical className="w-5 h-5 text-gray-800 dark:text-white" />
                </button>
                {isMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#2A2A2A] rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700">
                    <ul className="py-1">
                      {menuItems.map((item, index) => (
                        <li key={index}>
                          <button
                            onClick={item.onClick}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-500/30"
                          >
                            {item.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Chat Body */}
          <div
            ref={setContainerRef}
            className={`pt-4 px-6 py-6 overflow-y-auto flex-1 relative ${isKeyboardOpen ? "pb-20" : "pb-36"
              } md:pb-0`}
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,0.9), rgba(0,0,0,0.9)), url(${IMAGES.chatBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              overflowX: "hidden",
            }}
          >
            {messages.length === 0 ? (
              <div className="flex flex-col items-center mt-8">
                <div className="max-w-xl w-full border bg-white border-gray-300 dark:border-none dark:bg-[#222] rounded-xl py-6 px-4 flex items-center justify-center mx-6 md:mx-0">
                  <span className="text-primary text-sm md:text-lg text-center">
                    Messages are end-to-end encrypted. Only people in this chat can read them.
                  </span>
                </div>
              </div>
            ) : (
              formattedGroupedMessages.map(({ date, messages }) => {

                const visibleMessages = messages.filter((msg) => {
                  if (msg.type !== 'call') return true;
                  if (msg.callStatus !== 'missed') return false;

                  // group logic
                  return msg.missedReceiver?.[currentUser.uid];
                });

                // 🚀 STOP rendering date completely
                if (visibleMessages.length === 0) return null;

                return (
                  <div key={date} className="mb-6">
                    {visibleMessages.length > 0 && (
                      <div className="flex items-center justify-center mb-3">
                        <div className="bg-white/20 text-white px-8 py-1 rounded-lg text-sm font-medium backdrop-blur-md">
                          {date}
                        </div>
                      </div>
                    )}

                    <div className="space-y-4 z-10 relative pb-3 md:pb-0">
                      {visibleMessages.map((message) => {
                        const isFromCurrentUser = message.senderId === currentUser.uid;
                        const sender = users[message.senderId];
                        const swipeProgressValue = swipeProgress[message.id] || 0;

                        return (
                          <div
                            key={message.id}
                            data-message-id={message.id}
                            className={`flex relative items-start space-x-4 mt-3 ${isFromCurrentUser ? "justify-end" : "justify-start"
                              }`}
                            style={{ zIndex: 5 }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              openReactionBox(message.id, e.clientX, e.clientY, message);
                            }}
                            onTouchStart={(e) => {
                              handleSwipeStart(e, message.id);
                              longPressTimeout.current = setTimeout(() => {
                                if (!isScrollingRef.current && Math.abs(swipeProgressValue) < 20) {
                                  longPressTriggered.current = true;
                                  openReactionBox(message.id, e.touches[0].clientX, e.touches[0].clientY, message);
                                }
                              }, 600);
                            }}
                            onTouchMove={(e) => handleSwipeMove(e, message.id, isFromCurrentUser)}
                            onTouchEnd={() => {
                              clearTimeout(longPressTimeout.current);
                              handleSwipeEnd(message.id, () => handleReplyToMessageWithSender(message));
                            }}
                          >
                            {/* Swipe indicator */}
                            {swipeProgressValue !== 0 && (
                              <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                  transform: `translateX(${swipeProgressValue}px)`,
                                  zIndex: 10,
                                }}
                              >
                                <div
                                  className={`absolute top-1/2 transform -translate-y-1/2 ${isFromCurrentUser ? 'right-0' : 'left-0'
                                    } bg-primary text-white p-2 rounded-full opacity-75`}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                  </svg>
                                </div>
                              </div>
                            )}

                            <MessageBubble
                              message={message}
                              isFromCurrentUser={isFromCurrentUser}
                              currentUserId={currentUser.uid}
                              senderName={sender?.name || "Unknown"}
                              senderAvatar={sender?.profilePic || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"}
                              showHeader={message.showHeader}
                              onAvatarClick={() => navigate(`${ROUTES.VIEW_PROFILE}/${message.senderId}`)}
                              onImageClick={() => message.imageUrl && setSelectedImage(message.imageUrl)}
                              selectionMode={selectionMode}
                              selected={selectedMessage?.id === message.id}
                              onToggleReaction={toggleReaction}
                              chatType="group"
                              totalMembers={totalMembers}
                              loadedImages={loadedImages[message.id]}
                              onImageLoad={() => setLoadedImages(prev => ({ ...prev, [message.id]: true }))}
                            />

                            {/* Reaction Box */}
                            {selectedMessageId === message.id && (
                              <ReactionBox
                                position={reactionPosition}
                                isFromCurrentUser={isFromCurrentUser}
                                message={message}
                                currentUserId={currentUser.uid}
                                onReact={handleReaction}
                                onReply={handleReplyToMessageWithSender}
                                onEdit={handleEditMessage}
                                onDelete={handleDeleteMessage}
                                onClose={closeReactionBox}
                                boxRef={reactionBoxRef}
                                isHost={selectedGroup.createdBy === currentUser.uid}
                                showReply={true}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Chat Input */}
          <div
            className={`border-t dark:border-[#2A2A2A] border-[#D9D9D9]/50 bg-white dark:bg-[#1A1A1A] p-4 pb-6 md:pb-4 fixed ${isKeyboardOpen ? "bottom-0" : "bottom-18"
              } md:static left-0 right-0 z-10`}
          >
            <MessageInput
              ref={textareaRef}
              value={editingMessage ? editMessageText : newMessage}
              onChange={(e) => {
                if (editingMessage) {
                  setEditMessageText(e.target.value);
                } else {
                  setNewMessage(e.target.value);
                  updateTypingStatus(e.target.value.trim() !== "");
                }
                autoResizeTextarea(e.target);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  // Insert a newline at the cursor position
                  const { selectionStart, selectionEnd } = e.target;
                  const value = editingMessage ? editMessageText : newMessage;
                  const newValue =
                    value.substring(0, selectionStart) + "\n" + value.substring(selectionEnd);

                  if (editingMessage) {
                    setEditMessageText(newValue);
                  } else {
                    setNewMessage(newValue);
                  }

                  // Move cursor after the newline
                  setTimeout(() => {
                    e.target.selectionStart = e.target.selectionEnd = selectionStart + 1;
                  }, 0);

                  autoResizeTextarea(e.target);
                }
              }}
              onBlur={() => !editingMessage && updateTypingStatus(false)}
              placeholder={replyingTo ? "Reply to message..." : "Type a message..."}
              isUploading={isUploading}
              onCameraClick={() => fileInputRef.current.click()}
              onSend={() => editingMessage ? handleUpdateMessage() : handleSendMessage()}
              onRecordStart={startRecording}
              isRecording={isRecording}
              replyingTo={replyingTo}
              onClearReply={clearReply}
              currentUserId={currentUser.uid}
              senderName={replyingTo?.senderId === currentUser.uid ? "You" : replyingTo?.senderName}
            />

            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => {
                if (e.target.files[0]) {
                  handleImageUpload(e.target.files[0]);
                }
              }}
              accept="image/*"
              className="hidden"
              disabled={isUploading}
            />

            {recordingPopup && (
              <RecordingPopup
                onCancel={cancelRecording}
                onSend={stopRecording}
              />
            )}
          </div>
        </main>
      </div>

      {/* Bottom Sheets and Modals */}
      {isAddMembersOpen && (
        <AddGroupMembersBottomSheet
          allUsers={allUsers.filter((u) => u.uid !== selectedGroup.createdBy)}
          selectedGroup={selectedGroup}
          onAdd={handleAddMembers}
          onRemove={handleRemoveMembers}
          onClose={() => setIsAddMembersOpen(false)}
        />
      )}

      <ViewMembers
        isOpen={isMembersModalOpen}
        onClose={() => setIsMembersModalOpen(false)}
        membersPath={`Groups/${groupId}/members`}
        hostId={selectedGroup?.createdBy}
        title="Group Members"
      />

      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <img
            src={selectedImage}
            alt="Full view"
            className="max-h-[90%] max-w-[90%] rounded-lg shadow-lg object-contain"
          />
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-5 right-5 text-white text-xl md:text-3xl font-bold"
          >
            ✕
          </button>
          <button
            onClick={() => handleDownloadImage(selectedImage)}
            className="absolute bottom-5 right-5 bg-primary text-white p-3 rounded-full shadow-lg hover:bg-primary/90 transition"
            title="Download image"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
            </svg>
          </button>
        </div>
      )}

      {invitePopupOpen && groupId && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-xl w-[90%] max-w-md shadow-lg relative">
            <button
              onClick={() => { setInvitePopupOpen(false); clearInviteParam(); }}
              className="flex justify-self-end mb-2 text-gray-500 hover:text-gray-700 dark:hover:text-white text-xl font-bold"
            >
              ✕
            </button>
            <h2 className="text-lg font-semibold text-center mb-4 dark:text-white">
              You've been invited to join {selectedGroup.name}
            </h2>
            <p className="text-center mb-6 text-gray-600 dark:text-gray-300">
              Click below to join the group.
            </p>
            <button
              onClick={async () => {
                await handleInviteJoin(inviteToken);
                setInvitePopupOpen(false);
              }}
              className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary/90 transition"
            >
              Join Group
            </button>
          </div>
        </div>
      )}

      {/* Forward Share Popup */}
      <ForwardSharePopup
        isOpen={isSharePopupOpen}
        onClose={() => setIsSharePopupOpen(false)}
        onShare={handleForwardShare}
        groupId={groupId}
        currentGroupName={selectedGroup?.name}
      />
    </div>
  );
};

export default GroupChat;